import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BadgeIndianRupee, UserCheck } from "lucide-react";

const STATUS_CLS = {
  payment_pending: "border-[#FF8800]/40 text-[#FF8800]",
  paid: "border-[#00B0FF]/40 text-[#00B0FF]",
  assigned: "border-[#00B0FF]/40 text-[#00B0FF]",
  in_progress: "border-[#00E676]/40 text-[#00E676]",
  completed: "border-[#00E676]/40 text-[#00E676]",
};

export function PortalOrdersModule() {
  const [orders, setOrders] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/portal/admin/orders"), api.get("/portal/admin/specialists")])
      .then(([o, s]) => { setOrders(o.data); setSpecialists(s.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const assign = async (orderId, specialistId) => {
    try {
      await api.put(`/portal/admin/orders/${orderId}/assign`, { specialist_id: specialistId });
      toast.success("Specialist assigned");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markPaid = async (orderId) => {
    if (!window.confirm("Manually mark this order as PAID? This activates the service and assigns a specialist.")) return;
    try {
      await api.put(`/portal/admin/orders/${orderId}/mark-paid`);
      toast.success("Order marked as paid and activated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revenue = orders.filter((o) => o.status !== "payment_pending").reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <div data-testid="admin-orders-module">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold text-white">Service Orders</h2>
        <p className="text-sm text-slate-400">{orders.length} orders · ₹{revenue.toLocaleString("en-IN")} activated value</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400">No service orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const doneSteps = (o.workflow || []).filter((w) => w.status === "done").length;
            return (
              <div key={o.id} className="glass rounded-xl p-4 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`order-row-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-semibold text-white">{o.service_name}</p>
                    <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${STATUS_CLS[o.status] || STATUS_CLS.paid}`}>
                      {o.status.replace(/_/g, " ").toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {o.startup_name} · {o.founder_name} · ₹{o.total?.toLocaleString("en-IN")} · {doneSteps}/{(o.workflow || []).length} steps
                    {o.razorpay_payment_id && o.razorpay_payment_id !== "manual" && <span className="text-slate-600"> · {o.razorpay_payment_id}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
                  {o.status === "payment_pending" && (
                    <Button size="sm" variant="outline" data-testid={`order-mark-paid-${o.id}`} onClick={() => markPaid(o.id)} className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10">
                      <BadgeIndianRupee className="w-3.5 h-3.5 mr-1.5" /> Mark Paid
                    </Button>
                  )}
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-slate-500" />
                    <Select value={o.specialist_id || "none"} onValueChange={(v) => v !== "none" && assign(o.id, v)}>
                      <SelectTrigger data-testid={`order-assign-${o.id}`} className="bg-[#06080E] border-white/10 text-slate-200 w-48 h-9 text-xs">
                        <SelectValue placeholder="Assign specialist" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                        <SelectItem value="none">{o.specialist_name ? `Assigned: ${o.specialist_name}` : "Assign specialist..."}</SelectItem>
                        {specialists.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name} ({s.specialist_category})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
